import { TemplatePortal } from '@angular/cdk/portal';
import { AsyncPipe } from '@angular/common';
import {
  afterNextRender,
  Component,
  inject,
  OnDestroy,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef
} from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TranslatePipe } from '@cmi/addin-core';
import { map, Observable, switchMap } from 'rxjs';
import * as wordControls from '../../../lib/word-content-controls';
import { App } from '../../app';
import { Breadcrumb } from '../../shared/breadcrumb/breadcrumb';
import { ProviderTypeIcon } from '../../shared/provider-type-icon/provider-type-icon';
import { SearchBar } from '../../shared/search-bar/search-bar';
import { ContentProvider } from '../home.models';
import { HomeService } from '../home.service';
import { SearchResults } from '../search-results/search-results';

@Component({
  selector: 'cmi-content-providers',
  imports: [
    AsyncPipe,
    RouterModule,
    ProviderTypeIcon,
    Breadcrumb,
    SearchBar,
    SearchResults,
    TranslatePipe
  ],
  templateUrl: './content-providers.html'
})
export class ContentProviders implements OnDestroy {
  private readonly _route = inject(ActivatedRoute);
  private readonly _homeService = inject(HomeService);
  private readonly _viewContainerRef = inject(ViewContainerRef);
  private readonly _appComponent = inject(App);
  private readonly _footerPortal = viewChild.required<TemplateRef<unknown>>('footerPortal');

  protected readonly searchTerm = signal<string>('');

  public readonly tag$: Observable<string | null> = this._route.paramMap.pipe(
    map((params) => params.get('tag'))
  );

  public readonly contentProviders$ = this.tag$.pipe(
    map((tag) => tag ?? ''),
    switchMap((tag) => this._homeService.getContentProvidersByTag(tag ?? ''))
  );

  public constructor() {
    afterNextRender(() => {
      const portal = new TemplatePortal(this._footerPortal(), this._viewContainerRef);
      this._appComponent.footerPortal.set(portal);
    });
  }

  public ngOnDestroy(): void {
    this._appComponent.footerPortal.set(null);
  }

  public onSearchTermChanged(term: string): void {
    this.searchTerm.set(term);
  }

  public onCloseResults(): void {
    this.searchTerm.set('');
  }

  public async addProvider(provider: ContentProvider): Promise<void> {
    try {
      const options = {
        title: `CMI_${provider.key}`,
        placeholder: `${provider.key}`
      };
      if (provider.providerType === 'String') {
        await wordControls.insertRichTextControl(options);
      } else {
        await wordControls.insertTableControl(options);
      }
    } catch (error) {
      console.error('Error inserting content control:', error);
    }
  }
}
